import { defineStore, acceptHMRUpdate } from 'pinia';

import { getServices } from '~/db/services';

export const useAssessmentsStore = defineStore('AssessmentsStore', () => {
  if (!useAppRunningStore().isRunning) return;

  const { $db } = useNuxtApp();
  const { assessmentsService } = getServices($db);

  const { mainContent } = useContents();

  const context = useContextStore();

  const libraryBookId = computed(() => {
    return mainContent.value != null ? mainContent.value.id : '';
  });

  const memberId = computed(() => {
    return context?.memberId != null ? context.memberId : '';
  });

  const assessments = ref<AssessmentSchema[]>([]);

  const { assements: assessmentsDataSync } = useAssessmentsSyncGet();

  const debounceRefreshFromSync = useDebounceFn(() => {
    // We debounce because we clear all values from db before bulkPut and this makes two calls
    if (assessmentsDataSync.value && assessmentsDataSync.value.length > 0) {
      assessments.value = assessmentsDataSync.value;
    } else {
      assessments.value = [];
    }
  }, 100);

  watch(
    () => assessmentsDataSync.value,
    () => {
      debounceRefreshFromSync();
    }
  );

  const loadFromClient = async () => {
    const assessmentsData =
      await assessmentsService.findByLibraryBookIdAndMember(
        libraryBookId.value,
        memberId.value
      );
    if (assessmentsData) {
      assessments.value = assessmentsData;
      return true;
    }
    return false;
  };

  const forceUpdateRawValue = (rawValue) => {
    globalThis.$logger.info(
      'useAssessmentsStore forceUpdateRawValue',
      rawValue
    );
    assessments.value = rawValue;
  };

  const getAssessment = (uuid) => {
    const assessment = assessments.value.find((assessment) => {
      return assessment.uuid === uuid;
    });
    return assessment;
  };

  return {
    assessments: assessments as Readonly<Ref<AssessmentSchema[]>>,
    loadFromClient,
    forceUpdateRawValue,
    getAssessment,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAssessmentsStore, import.meta.hot));
}
